import Image from "next/image";
import { cn } from "@repo/ui/lib/utils";
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Price } from "@components/price";
import { Product, Variant } from "@/types/product-search";
import { sanifyProductTitle } from "@repo/next-shared/utils";
import SizeBadge from "@components/size-badge";
import { Highlight } from "react-instantsearch";
import { Hit } from "algoliasearch";
import { useNormalizedColors } from "@components/search/hooks/useNormalizedColors";

export const ProductListItem = ({
  product,
  hit,
}: {
  product: Product & { productId?: string; colorsNormalized?: string[] };
  hit?: Hit;
}) => {
  const t = useTranslations();
  const colorsMap = useNormalizedColors();

  /*function getLowestVariantPrice(product: Product) {
    return product.variants.reduce((min, variant) =>
      variant.targetPrice < min.targetPrice ? variant : min,
    );
  }*/

  function getDefaultVariant(product: Product): Variant | undefined {
    return product.variants.find((variant: Variant) => variant.defaultVariant);
  }

  return (
    <Link
      href={`/product/${product?.id || product?.productId}/${sanifyProductTitle(product?.title)}`}
    >
      <div className="overflow-visible flex flex-col min-h-full">
        <div
          className={cn(
            "p-2 pt-6 relative w-[100%] aspect-[10/12] max-md:aspect-[37/50] overflow-hidden rounded-lg dark:shadow-none bg-white",
            "after:absolute after:top-0 after:left-0 after:w-full after:h-full after:bg-black after:opacity-[.03] after:dark:opacity-[.1] after:pointer-events-none",
          )}
        >
          <Image
            data-loaded="false"
            className={cn(
              "box-border w-[100%] object-cover data-[loaded=false]:animate-pulse data-[loaded=false]:bg-secondary-gray3/100 dark:data-[loaded=false]:bg-secondary-gray3/10 z-1",
            )}
            src={
              decodeURIComponent(
                getDefaultVariant(product)?.catalogImgUrl || "",
              ) || ""
            }
            fill
            sizes="(min-width: 768px) 50vw, 100vw"
            style={{
              objectFit: "contain", // cover, contain, none
            }}
            onLoad={(event) => {
              event.currentTarget.setAttribute("data-loaded", "true");
            }}
            alt={product?.title}
          />
        </div>
        <h5 className="capitalize font-bold mt-4 max-md:mt-3 max-md:text-base leading-6 max-md:leading-[18px] line-clamp-1">
          {product?.brand}
        </h5>
        <span className="capitalize max-md:text-sm mt-2 max-md:mt-3 font-extralight leading-tight line-clamp-2 min-h-[2lh]">
          {hit ? (
            <Highlight
              attribute="titleIt"
              hit={hit as any}
              classNames={{
                highlighted:
                  "bg-primary-magenta text-primary-white font-normal",
              }}
            />
          ) : (
            product?.title
          )}
        </span>
        <div className="flex-1">
          <SizeBadge product={product} />
          {/*{product?.colorsNormalized && product.colorsNormalized.length > 0 && (*/}
          {/*  <div className="w-full pt-2 flex gap-4 items-center">*/}
          {/*    {product.colorsNormalized.map((color: string) => (*/}
          {/*      <div*/}
          {/*        key={color}*/}
          {/*        className={cn(*/}
          {/*          "rounded-full h-[24px] w-[24px] border-secondary-gray4 dark:border-primary-white border-[1.5px]",*/}
          {/*        )}*/}
          {/*        style={{*/}
          {/*          backgroundColor: colorsMap?.[color]?.value,*/}
          {/*        }}*/}
          {/*      />*/}
          {/*    ))}*/}
          {/*  </div>*/}
          {/*)}*/}
        </div>
        <div className="block max-md:text-base leading-[14px] font-bold text-sm mt-3 max-md:mt-1 max-md:leading-none">
          {product?.minVariantTargetPrice && (
            <>
              <span className="leading-none">
                {t("Products-list.price-from")}{" "}
              </span>
              <br />
              <Price
                className="mt-1"
                value={product?.minVariantTargetPrice || 0}
              />
            </>
          )}
        </div>
      </div>
    </Link>
  );
};
